import { useEffect, useRef } from 'react';
import { useChatStore } from '../stores/chat';
import { MessageBubble } from './MessageBubble';
import { MessageInput } from './MessageInput';
import { StatusIndicator } from './StatusIndicator';

export function ChatArea() {
  const {
    activeConversationId,
    activeMessages,
    isWaiting,
    pendingMessage,
    isConnected,
    error,
    clearError,
    loadingPhase,
    activeTool,
  } = useChatStore();

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeMessages, pendingMessage]);

  if (!activeConversationId) {
    return (
      <div className="flex-1 flex items-center justify-center bg-neutral-950">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-900/30 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-primary-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Welcome to Binks Chat</h2>
          <p className="text-neutral-400 text-sm">Select a conversation or start a new one</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-neutral-950">
      {/* Error banner */}
      {error && (
        <div className="bg-red-900/50 border-b border-red-800 px-4 py-2 flex items-center justify-between">
          <span className="text-red-200 text-sm">{error}</span>
          <button onClick={clearError} className="text-red-300 hover:text-red-100">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Connection status */}
      {!isConnected && (
        <div className="bg-yellow-900/30 border-b border-yellow-800/50 px-4 py-1.5 text-center">
          <span className="text-yellow-200 text-xs">Connecting...</span>
        </div>
      )}

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto chat-scrollbar px-4 py-6">
        <div className="max-w-3xl mx-auto space-y-4">
          {activeMessages.map((message) => (
            <MessageBubble key={message.id} message={message} />
          ))}

          {/* Streaming response */}
          {pendingMessage && (
            <MessageBubble
              message={{
                id: 'streaming',
                conversation_id: activeConversationId,
                role: 'assistant',
                content: pendingMessage,
                created_at: new Date().toISOString(),
              }}
              isStreaming
            />
          )}

          {/* Status indicator for thinking/tool calling */}
          {isWaiting && loadingPhase !== 'idle' && loadingPhase !== 'generating' && (
            <StatusIndicator
              loadingPhase={loadingPhase}
              activeTool={activeTool}
              pendingMessage={pendingMessage}
            />
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input area */}
      <MessageInput />
    </div>
  );
}
